/**
 * Estrae voci TOC da titoli ## nel markdown (esclude ###).
 * Gli id devono coincidere con quelli assegnati ai <h2> in render.
 */

import type { ReactNode } from 'react';

/** Testo piano da children react-markdown (per slug h3). */
export function markdownNodeToPlainText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(markdownNodeToPlainText).join('');
  if (typeof node === 'object' && 'props' in node) {
    const el = node as { props?: { children?: ReactNode } };
    return markdownNodeToPlainText(el.props?.children);
  }
  return '';
}

export function stripInlineMarkdown(raw: string): string {
  return raw
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();
}

/**
 * Rimuove enumerazione iniziale tipo "12. " o "3) " dai titoli markdown.
 * Evita doppia numerazione se il TOC usa elenco proprio o solo testo senza `<ol>`.
 */
export function stripLeadingEnumeratedPrefix(s: string): string {
  return s
    .replace(/^\d+\.\s+/, '')
    .replace(/^\d+\)\s+/, '')
    .trim();
}

export function slugifyHeading(text: string): string {
  const ascii = stripInlineMarkdown(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  const slug = ascii.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return slug || 'section';
}

export interface MarkdownTocItem {
  id: string;
  label: string;
}

/** Elenco h2 in ordine di apparizione, con id univoci. */
export function extractMarkdownH2Toc(markdown: string): MarkdownTocItem[] {
  const lines = markdown.split(/\r?\n/);
  const slugCounts = new Map<string, number>();
  const out: MarkdownTocItem[] = [];

  for (const line of lines) {
    if (!/^##\s+/.test(line) || /^###/.test(line)) continue;
    const raw = line.replace(/^##\s+/, '').trim();
    const plain = stripInlineMarkdown(raw);
    if (!plain) continue;

    const label = stripLeadingEnumeratedPrefix(plain).trim() || plain.trim();

    const base = slugifyHeading(label);
    const n = slugCounts.get(base) ?? 0;
    slugCounts.set(base, n + 1);
    const id = n === 0 ? base : `${base}-${n + 1}`;
    out.push({ id, label });
  }

  return out;
}
